<template>
  <router-link :to="`/administration/containers/${container.id}`" class="tile" :class="container.health" :title="tooltip">
    <div class="tile-name">
      <span class="dot"></span>
      <strong class="name">{{ container.id }}</strong>
    </div>
    <div class="tile-place">
      <code v-if="container.port !== null && container.port !== undefined">:{{ container.port }}</code>
      <code v-if="container.exposure === 'gateway'" class="route">/{{ container.route }}</code>
      <span v-else-if="container.exposure === 'internal'" class="internal">interne</span>
    </div>
    <div v-if="container.metrics" class="tile-use">
      <span v-if="memoryPercent !== null" class="meter" :title="`Mémoire : ${integer(memoryPercent)} % de la limite du container`"><i :class="memoryTone" :style="{width: `${Math.min(100, memoryPercent)}%`}"></i></span>
      <span class="memory" title="Mémoire du container">{{ bytes(memory.used) }}</span>
      <span class="cpu" title="CPU du container (100 % = un cœur)">{{ cpu }} %</span>
    </div>
    <div v-else class="tile-use off" :class="badge.tone">{{ badge.label }}</div>
    <div v-if="container.alerts.length" class="tile-alert" :class="alertLevel" :title="alertMessages">{{ alertText }}</div>
  </router-link>
</template>

<script>
import {integer} from "@/statistics/utils/format";
import {bytes, containerBadge, duration, usageTone} from "@/administration/utils";

/**
 * Tuile d'un container : état, port et route, mémoire, CPU et alerte éventuelle. Le détail (description, uptime, réponse, base, requêtes) est dans l'infobulle et dans la page du container, que la tuile ouvre.
 * thresholds : seuils d'alerte renvoyés par la passerelle.
 */
export default {
  name: 'ContainerCard',
  props: {
    container: {type: Object, required: true},
    thresholds: {type: Object, required: true},
  },
  computed: {
    badge() {
      return containerBadge(this.container);
    },
    // Mémoire du container (comme docker la compte), à défaut celle du processus
    memory() {
      const metrics = this.container.metrics;
      const memory = metrics.container.memory;
      return memory ? {used: memory.usedBytes, limit: memory.limitBytes} : {used: metrics.process.rssBytes, limit: null};
    },
    memoryPercent() {
      return this.memory.limit ? this.memory.used / this.memory.limit * 100 : null;
    },
    memoryTone() {
      return usageTone(this.memoryPercent ?? 0, this.thresholds.containerMemoryPercent);
    },
    cpu() {
      const metrics = this.container.metrics;
      return integer(metrics.container.cpuPercent ?? metrics.process.cpuPercent);
    },
    alertLevel() {
      return this.container.alerts.some(alert => alert.level === 'critical') ? 'critical' : 'warning';
    },
    alertText() {
      const alerts = this.container.alerts;
      return alerts.length === 1 ? alerts[0].message : `${alerts.length} alertes`;
    },
    alertMessages() {
      return this.container.alerts.map(alert => alert.message).join('\n');
    },
    // Tout ce que la tuile ne montre pas
    tooltip() {
      const container = this.container;
      const lines = [`${container.id} : ${container.description}`, `Container ${container.container} · ${this.badge.label}`];
      const metrics = container.metrics;
      if (metrics) {
        lines.push(`En ligne depuis ${duration(metrics.uptimeSeconds)} · réponse en ${container.responseMs} ms`);
        if (metrics.database.status !== 'none') lines.push(`Base de données : ${metrics.database.status === 'up' ? `${metrics.database.pingMs} ms` : 'injoignable'}`);
        if (metrics.requests) lines.push(`Requêtes : ${integer(metrics.requests.total)}${metrics.requests.statuses['5xx'] ? ` (${metrics.requests.statuses['5xx']} erreurs)` : ''}`);
      }
      return lines.join('\n');
    },
  },
  methods: {integer, bytes},
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.tile
  display flex
  flex-direction column
  gap 4px
  min-width 0
  padding 8px 10px
  color inherit
  text-decoration none
  background white
  border 1px solid #e5e5e5
  border-radius 10px
  transition box-shadow .2s

  &:hover
    box-shadow 0 2px 8px rgba(0,0,0,.1)

  &.warning
    border-color #f0d9a3

  &.critical
    border-color #f3c6cd

.tile-name
  display flex
  align-items center
  gap 7px

.dot
  flex none
  width 9px
  height 9px
  background #3fbd63
  border-radius 50%

  .warning &
    background #ef9400

  .critical &
    background #ea4e2c

  .unknown &
    background #c2cbdc

.name
  min-width 0
  overflow hidden
  font-size 13px
  color THEME_COLOR_PRIMARY
  text-overflow ellipsis
  white-space nowrap

.tile-place
  display flex
  flex-wrap wrap
  gap 0 6px
  font-size 11.5px
  color CTRL_TEXT_MUTED

  code
    font-weight 600
    color THEME_COLOR_PRIMARY

.internal
  color #7f899e

.tile-use
  display flex
  align-items center
  gap 7px
  font-size 11.5px
  color #3f3931

  &.off
    color CTRL_ERROR

    &.muted
      color CTRL_TEXT_MUTED

.meter
  flex 1 1 24px
  height 4px
  overflow hidden
  background #eef1f6
  border-radius 2px

  i
    display block
    height 100%
    background #3fbd63

    &.warning
      background #ef9400

    &.critical
      background #ea4e2c

.memory
  white-space nowrap

.cpu
  margin-left auto
  white-space nowrap

.tile-alert
  overflow hidden
  font-size 11.5px
  text-overflow ellipsis
  white-space nowrap

  &.warning
    color #8a5a00

  &.critical
    color CTRL_ERROR
</style>
